/* eslint-disable @next/next/no-img-element, react/no-danger, react/forbid-component-props */
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { REVIEWS_IO, TRIP_ADVISOR } from '../../config/constants/images';
import { REVIEWS_PER_PAGE, REVIEWS_TO_SHOW } from '../../config/setup/setup';
import { REVIEWS_IO_ALT, TRIP_ADVISOR_ALT } from '../../config/text/text';
import { showNationalReviews } from '../../helpers/cookieSetter';
import { getShowReviews } from '../../helpers/deals';
import ThemeContext from '../../providers/ThemeProvider';
import MoreButton from '../_generic/more-button/MoreButton';
import ScrollToOffset from '../_generic/scroll-to-offset/ScrollToOffset';
import Stars from '../_generic/stars/Stars';
import MainDealReviewsPagination from './MainDealReviewsPagination';
import MainDealReviewItem from './mainDealReviewItem';
import CollapsableSection from './shared-components/CollapsableSection';

const MainDealReviews = ({
  deal,
  className = '',
  collapsed = false,
  pagination = false,
  onToggle = () => false,
  isTravel = false,
}) => {
  const theme = useContext(ThemeContext);
  const [showAll, setShowAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reviews, setReviews] = useState(deal?.reviews || []);
  const reviewsPerPage = REVIEWS_PER_PAGE;

  const getPageContent = (currentPageNo) => {
    const startIndex = (currentPageNo - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    const updatedReviews = deal?.reviews?.slice(startIndex, endIndex);
    setReviews(updatedReviews);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getPageContent(currentPage);
  }, [currentPage]);

  if (!getShowReviews(deal, isTravel)) {
    return null;
  }

  const moreToShow = deal.reviews.length > REVIEWS_TO_SHOW;

  if (!showNationalReviews() && !isTravel) return null;

  return (
    <>
      <div className="reviews">
        <ScrollToOffset id="reviews" />
        <CollapsableSection
          className={className}
          collapsed={collapsed}
          onToggle={onToggle}
          title={
            <>
              <span style={{ marginRight: 10 }}>
                Reviews ({deal.reviewSummary.total || deal.reviews.length})
              </span>
              <Stars
                className="stars--small"
                value={deal.reviewSummary.average}
              />
            </>
          }
        >
          {deal.reviewTitle && isTravel && (
            <div className="review-title">
              <img
                alt={TRIP_ADVISOR_ALT}
                className="logo"
                height={20}
                src={TRIP_ADVISOR}
                width={34}
              />
              <div dangerouslySetInnerHTML={{ __html: deal.reviewTitle }} />
            </div>
          )}
          {deal.reviewTitle && !isTravel && (
            <div className="review-title">
              <img
                alt={REVIEWS_IO_ALT}
                className="logo"
                height={34}
                src={REVIEWS_IO}
                width={34}
              />
              <div dangerouslySetInnerHTML={{ __html: deal.reviewTitle }} />
            </div>
          )}
          {reviews.map((review, index) => {
            return (
              <MainDealReviewItem
                hidden={!showAll && index + 1 > REVIEWS_TO_SHOW}
                key={index}
                review={review}
              />
            );
          })}
          {showAll && pagination && (
            <MainDealReviewsPagination
              itemsPerPage={REVIEWS_PER_PAGE}
              onPageChange={handlePageChange}
              openedPage={currentPage}
              totalItems={deal.reviews.length}
            />
          )}
          {moreToShow && pagination && currentPage === 1 && (
            <div className="reviews__more">
              <MoreButton
                expanded={showAll}
                onToggle={() => setShowAll((state) => !state)}
              />
            </div>
          )}
        </CollapsableSection>
      </div>

      <style jsx>{`
        .reviews__more {
          margin-bottom: 10px;
        }
        .review-title {
          display: flex;
          margin-bottom: 10px;
          align-items: center;
        }
        .logo {
          margin-right: 10px;
        }
        .review-title :global(a) {
          color: ${theme.colors.primary};
        }
      `}</style>
    </>
  );
};

MainDealReviews.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  deal: PropTypes.object,
  isTravel: PropTypes.bool,
  onToggle: PropTypes.func,
  pagination: PropTypes.bool,
};

export default MainDealReviews;
